import {
  useFilterStore,
  useMapStore,
  usePointStore,
  usePolygonStore,
} from '../../../../state-library/src'
import { useEffect, useMemo, useRef } from 'react'
import { EHeatMapCategory, ESearchLevel } from '../../../../types-library/src'
import { tileZoomConfiguration } from '../../utils'
import {
  animateChangeHeatMapByColors,
  animateHoverPolygon,
  heatMapColors,
  presenterPolygonProperties,
} from '../../utils/heatMapUtils'
import PolygonPopup from './polygonPopup'
import { usePrevious } from '@wiggot/hooks/src/usePrevious'

const StatePolygonManager = (): JSX.Element => {
  const { map, isLoadedMap, drawingTool, hoveredLayer, setIsFlyTo } =
    useMapStore()

  const {
    polygonTiles,
    heatMapCategory,
    isEnabledHeatMap,

    setPolygonNavigation,
    clearPolygonNavigation,
  } = usePolygonStore()

  const { pointsSvgContainer } = usePointStore()

  const { setSearchLevel, setLatitude, setLongitude } = useFilterStore()

  const heatMapCategoryPrevious = usePrevious<EHeatMapCategory | null>(
    heatMapCategory
  )

  const pointsSvgContainerRef = useRef<any>(pointsSvgContainer)
  pointsSvgContainerRef.current = pointsSvgContainer

  const drawingToolRef = useRef<typeof drawingTool>(drawingTool)
  drawingToolRef.current = drawingTool

  const hoveredLayerRef = useRef<typeof hoveredLayer>(hoveredLayer)
  hoveredLayerRef.current = hoveredLayer

  const heatMapCategoryRef = useRef<EHeatMapCategory | null>(heatMapCategory)
  heatMapCategoryRef.current = heatMapCategory

  const stateTiles = useMemo(() => {
    const filter = polygonTiles.filter((tile) => tile?.tiles?.state?.id)
    return filter.map((tile) => tile.tiles.state)
  }, [polygonTiles])

  const loadStateLevel = (params: { category: EHeatMapCategory }): void => {
    if (!map) {
      return
    }

    const { category } = params

    for (const { id } of stateTiles) {
      const layerName = `${id}-state-polygon`
      const source = `${id}-state-source`
      const sourceLayer = id

      const find = map?.getSource(source)

      if (find) {
        continue
      }

      const { minZoom, maxZoom } = tileZoomConfiguration.state

      map?.addSource(source, {
        type: 'vector',
        url: `mapbox://davpach.${id}`,
        minzoom: minZoom,
        maxzoom: maxZoom,
      })

      map?.addLayer({
        id: layerName,
        type: 'fill',
        source,
        'source-layer': sourceLayer,
        paint: {
          'fill-color': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            ['feature-state', 'hover_color'],
            heatMapColors[category].medium_high,
          ],
          'fill-opacity': 0.4,
        },
        minzoom: minZoom,
        maxzoom: maxZoom,
      })

      map?.addLayer({
        id: `${id}-state-border`,
        type: 'line',
        source,
        'source-layer': sourceLayer,
        paint: {
          'line-color': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            heatMapColors[category].hover,
            '#0368FE',
          ],
          'line-width': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            ['feature-state', 'hover_width'],
            1.678,
          ],
        },
        minzoom: minZoom,
        maxzoom: maxZoom,
      })

      createHoverEvent({ layerName, source, sourceLayer })
      createClickEvent({ layerName })
    }
  }
  const heatMapColor = (heatMapCategoryRef?.current ?? heatMapCategory)as keyof (typeof heatMapColors)

  const createHoverEvent = (params: {
    layerName: string
    source: string
    sourceLayer: string
  }) => {
    const { layerName, source, sourceLayer } = params

    let featureId: number | undefined

    map?.on('mousemove', layerName, ({ features }) => {
      if (!features?.length) {
        return
      }

      const feature = features[0]

      if (!feature) {
        return
      }

      const { id } = feature

      if (featureId && featureId === id) {
        return
      }

      featureId = id as number

      if (
        !featureId ||
        (drawingToolRef.current.isActive &&
          drawingToolRef.current.status !== 'processed') ||
        hoveredLayerRef?.current?.layerId
      ) {
        return
      }

      setTimeout(() => {
        pointsSvgContainerRef?.current?.style('cursor', 'pointer')
        map.getCanvas().style.cursor = 'pointer'
      }, 50)

      animateHoverPolygon({
        source,
        sourceLayer,
        featureId,
        previousBorder: 1.678,
        nextBorder: 4,
        bgColor:
          heatMapColors[heatMapColor]
            .medium_high,
        bgHoverColor:
          heatMapColors[heatMapColor].hover,
        duration: 300,
        map,
      })

      map?.setFeatureState(
        {
          source,
          sourceLayer,
          id: featureId,
        },
        {
          hover: true,
          hover_color:
            heatMapColors[heatMapColor]
              .medium_high,
          hover_width: 1.678,
        }
      )
    })

    map?.on('mouseleave', layerName, () => {
      if (
        !featureId ||
        (drawingToolRef.current.isActive &&
          drawingToolRef.current.status !== 'processed')
      ) {
        return
      }

      pointsSvgContainerRef?.current?.style('cursor', 'grab')
      map.getCanvas().style.cursor = 'grab'

      map?.setFeatureState(
        {
          source,
          sourceLayer,
          id: featureId,
        },
        { hover: false }
      )

      featureId = undefined
    })
  }

  const createClickEvent = (params: { layerName: string }) => {
    const { layerName } = params

    map?.on('click', layerName, (event) => {
      if (
        (drawingToolRef.current.isActive &&
          drawingToolRef.current.status !== 'processed') ||
        hoveredLayerRef?.current?.layerId
      ) {
        return
      }

      map?.flyTo({
        center: event.lngLat,
        essential: true,
        speed: 1.4,
        curve: 1.3,
        duration: 1000,
        zoom: tileZoomConfiguration.state.maxZoom + 0.1,
      })

      setIsFlyTo(true)

      setTimeout(() => {
        setIsFlyTo(false)
      }, 1000)

      if (!event?.features?.length) {
        return
      }

      clearPolygonNavigation({
        state: true,
        municipality: true,
        neighborhood: true,
      })

      const properties = presenterPolygonProperties({
        properties: event?.features[0]?.properties,
      })

      setPolygonNavigation({
        level: ESearchLevel.STATE,
        properties,
      })

      setSearchLevel(ESearchLevel.STATE)
      setLatitude(properties?.centroid?.lat ?? 0)
      setLongitude(properties?.centroid?.lng ?? 0)
    })
  }

  const removeStateLevel = (): void => {
    if (!map) {
      return
    }

    for (const { id } of stateTiles) {
      const find = map?.getSource(`${id}-state-source`)

      if (!find) {
        continue
      }

      const layerName = `${id}-state-polygon`
      const source = `${id}-state-source`

      map.removeLayer(layerName)
      map.removeLayer(`${id}-state-border`)
      map.removeSource(source)
    }
  }

  const changeStateLevelHeatMapColor = (params: {
    category: EHeatMapCategory
    previousCategory: EHeatMapCategory
  }): void => {
    if (!map) {
      return
    }

    const { category, previousCategory } = params

    for (const { id } of stateTiles) {
      const find = map?.getSource(`${id}-state-source`)

      if (!find) {
        continue
      }

      animateChangeHeatMapByColors({
        layerName: `${id}-state-polygon`,
        previousColor: heatMapColors[previousCategory].medium_high,
        nextColor: heatMapColors[category].medium_high,
        duration: 300,
        map,
      })

      map.setPaintProperty(`${id}-state-border`, 'line-color', [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        heatMapColors[category].hover,
        '#0368FE',
      ])
    }
  }

  useEffect(() => {
    if (map && isLoadedMap && polygonTiles.length) {
      if (isEnabledHeatMap) {
        loadStateLevel({ category: heatMapCategory })
      } else {
        removeStateLevel()
      }
    }
  }, [map, isLoadedMap, polygonTiles, isEnabledHeatMap])

  useEffect(() => {
    if (heatMapCategory && isEnabledHeatMap && map) {
      changeStateLevelHeatMapColor({
        category: heatMapCategory,
        previousCategory: heatMapCategoryPrevious ?? heatMapCategory,
      })
    }
  }, [heatMapCategory])

  return (
    <>
      {stateTiles.map((state) => (
        <PolygonPopup
          key={state.id}
          layer={`${state.id}-state-polygon`}
          level="state"
        />
      ))}
    </>
  )
}

export default StatePolygonManager
